// Profile Creation Modal - First-time user onboarding
import React, { useState, useRef, useCallback } from 'react';
import './ProfileCreationModal.css';

const ProfileCreationModal = ({ 
  isOpen, 
  onClose, 
  onCreateProfile, 
  walletAddress,
  isCreating = false,
  checkUsernameAvailability: externalCheckUsername
}) => {
  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    avatarUrl: ''
  });
  const [errors, setErrors] = useState({});
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const usernameCheckTimer = useRef(null);

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Check username availability with debounce
    if (field === 'username') {
      setUsernameAvailable(null);
      // Clear any pending check
      if (usernameCheckTimer.current) {
        clearTimeout(usernameCheckTimer.current);
      }
      if (value.length >= 3) {
        setUsernameChecking(true);
        usernameCheckTimer.current = setTimeout(() => {
          checkUsernameAvailability(value);
        }, 600);
      } else {
        setUsernameChecking(false);
      }
    }
  };

  const checkUsernameAvailability = async (username) => {
    setUsernameChecking(true);
    try {
      // Reserved usernames that can never be used
      const reservedUsernames = ['admin', 'znoospace', 'test', 'user', 'moderator', 'support', 'system'];
      if (reservedUsernames.includes(username.toLowerCase())) {
        setUsernameAvailable(false);
        return;
      }

      // Use quickOnly for real-time typing — only fast local checks (no API calls)
      if (externalCheckUsername) {
        const result = await externalCheckUsername(username, { quickOnly: true });
        setUsernameAvailable(result.available);
      } else {
        setUsernameAvailable(true);
      }
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameAvailable(null);
    } finally {
      setUsernameChecking(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    } else if (usernameAvailable === false) {
      newErrors.username = 'Username is already taken';
    }

    if (formData.bio.length > 160) {
      newErrors.bio = 'Bio must be 160 characters or less';
    }

    if (formData.avatarUrl && !isValidUrl(formData.avatarUrl)) {
      newErrors.avatarUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('📝 ProfileCreationModal: Form submitted with data:', formData);
    
    if (!validateForm()) {
      console.log('❌ ProfileCreationModal: Form validation failed');
      return;
    }

    // Run FULL username check (all 4 layers including blockchain) before submit
    if (externalCheckUsername && formData.username.trim().length >= 3) {
      setUsernameChecking(true);
      try {
        const fullCheck = await externalCheckUsername(formData.username, { quickOnly: false });
        if (!fullCheck.available) {
          setUsernameAvailable(false);
          setErrors(prev => ({ ...prev, username: 'Username is already taken' }));
          setUsernameChecking(false);
          return;
        }
      } catch (error) {
        console.error('Error in full username check:', error);
      } finally {
        setUsernameChecking(false);
      }
    }

    console.log('✅ ProfileCreationModal: Form validation passed');
    console.log('📝 ProfileCreationModal: onCreateProfile function exists:', typeof onCreateProfile);
    
    if (onCreateProfile) {
      try {
        console.log('📝 ProfileCreationModal: Calling onCreateProfile...');
        await onCreateProfile(formData);
        console.log('✅ ProfileCreationModal: onCreateProfile completed successfully');
      } catch (error) {
        console.error('❌ ProfileCreationModal: onCreateProfile failed:', error);
      }
    } else {
      console.error('❌ ProfileCreationModal: onCreateProfile function not provided');
    }
  };

  const handleClose = () => {
    if (!isCreating && onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay">
      <div className="profile-modal">
        <div className="profile-modal-header">
          <h2>🎉 Welcome to Znoospace!</h2>
          <p>Let's create your cosmic profile to start beaming</p>
          {!isCreating && (
            <button className="close-btn" onClick={handleClose}>×</button>
          )}
        </div>

        <div className="wallet-info">
          <div className="wallet-badge">
            <span className="wallet-icon">🌙</span>
            <span className="wallet-address">{formatAddress(walletAddress)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="username">
              Username <span className="required">*</span>
            </label>
            <div className="username-input-container">
              <input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Enter your cosmic username"
                className={`form-input ${errors.username ? 'error' : ''} ${usernameAvailable === true ? 'success' : ''}`}
                disabled={isCreating}
              />
              <div className="username-status">
                {usernameChecking && <span className="checking">⏳</span>}
                {usernameAvailable === true && <span className="available">✅</span>}
                {usernameAvailable === false && <span className="unavailable">❌</span>}
              </div>
            </div>
            {errors.username && <span className="error-text">{errors.username}</span>}
            {usernameAvailable === true && <span className="success-text">Username is available!</span>}
            {usernameAvailable === false && <span className="error-text">Username is already taken</span>}
          </div>

          <div className="form-group">
            <label htmlFor="bio">Bio (optional)</label>
            <textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              placeholder="Share your cosmic story with the universe..."
              className={`form-textarea ${errors.bio ? 'error' : ''}`}
              rows="3"
              maxLength="160"
              disabled={isCreating}
            />
            <div className="char-count">
              {formData.bio.length}/160
            </div>
            {errors.bio && <span className="error-text">{errors.bio}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="avatarUrl">Avatar URL (optional)</label>
            <input
              id="avatarUrl"
              type="url"
              value={formData.avatarUrl}
              onChange={(e) => handleInputChange('avatarUrl', e.target.value)}
              placeholder="https://example.com/avatar.jpg"
              className={`form-input ${errors.avatarUrl ? 'error' : ''}`}
              disabled={isCreating}
            />
            {errors.avatarUrl && <span className="error-text">{errors.avatarUrl}</span>}
          </div>

          <div className="transaction-info">
            <div className="info-box">
              <span className="info-icon">💰</span>
              <div className="info-text">
                <strong>Cosmic Profile Creation</strong>
                <p>Your profile will be permanently stored on the Solana blockchain. A small SOL transaction fee is required.</p>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="create-profile-btn"
              disabled={isCreating || usernameAvailable === false || !formData.username.trim()}
            >
              {isCreating ? (
                <>
                  <span className="spinner">⏳</span>
                  Creating Profile...
                </>
              ) : (
                <>
                  <span className="create-icon">✨</span>
                  Create Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileCreationModal;
